import type {ReactElement} from 'react';
import {useCallback, useEffect, useState} from 'react';
import type {TargetAndTransition} from 'framer-motion';
import {motion} from 'framer-motion';
import {getAssetUrl} from '@Utils/s3.util';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import {CARD_POSES, CardRole, CYCLE_INTERVAL_MS, TRANSITION_DURATION_S} from './change-style-announcement-visual.types';
import styles from './change-style-announcement-visual.module.scss';

const CARD_IMAGE_URLS = [
  getAssetUrl('ai-chat-change-style-announcement-1.webp'),
  getAssetUrl('ai-chat-change-style-announcement-2.webp'),
  getAssetUrl('ai-chat-change-style-announcement-3.webp'),
];

const ROLE_BY_OFFSET: CardRole[] = [CardRole.FRONT, CardRole.BACK_LEFT, CardRole.BACK_RIGHT];

function getPoseForCard(cardIndex: number, activeIndex: number, cardCount: number): TargetAndTransition {
  const offset = (activeIndex - cardIndex + cardCount) % cardCount;
  return CARD_POSES[ROLE_BY_OFFSET[offset]];
}

interface CardImageProps {
  src: string;
  isSettled: boolean;
  onSettle: () => void;
}

function CardImage({src, isSettled, onSettle}: CardImageProps): ReactElement {
  return (
    <>
      {isSettled ? null : (
        <SkeletonBrick
          theme={SkeletonBrickTheme.LIGHT}
          isPulsating={false}
          className={styles.cardSkeleton}
          radius="4px"
        />
      )}
      <img
        src={src}
        alt=""
        className={styles.cardImage}
        data-loaded={isSettled}
        onLoad={onSettle}
        onError={onSettle}
      />
    </>
  );
}

export function ChangeStyleAnnouncementVisual(): ReactElement {
  const [activeIndex, setActiveIndex] = useState(0);
  const [settledIndices, setSettledIndices] = useState<ReadonlySet<number>>(new Set());
  const allSettled = settledIndices.size >= CARD_IMAGE_URLS.length;

  const handleCardSettle = useCallback((index: number): void => {
    setSettledIndices((prev) => {
      if (prev.has(index)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!allSettled) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % CARD_IMAGE_URLS.length);
    }, CYCLE_INTERVAL_MS);
    return (): void => {
      window.clearInterval(id);
    };
  }, [allSettled]);

  return (
    <div className={styles.stack}>
      {CARD_IMAGE_URLS.map((src, index) => (
        <motion.div
          key={src}
          className={styles.card}
          initial={false}
          animate={getPoseForCard(index, activeIndex, CARD_IMAGE_URLS.length)}
          transition={{duration: TRANSITION_DURATION_S, ease: 'easeInOut'}}
        >
          <CardImage
            src={src}
            isSettled={settledIndices.has(index)}
            onSettle={(): void => {
              handleCardSettle(index);
            }}
          />
        </motion.div>
      ))}
    </div>
  );
}
